import { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import type { StructureModel } from '../nbt/types';
import { buildStructureGeometry, type GeometryData, type RenderMode } from '../render/buildGeometry';
import { buildStructureAssets, type ResourcePackTextures } from '../render/structureAssets';

export interface ViewerStats {
  faces: number;
  textures: number;
  sourced: number;
}

interface StructureViewerProps {
  model: StructureModel;
  mode: RenderMode;
  pack: ResourcePackTextures | null;
  /** `model.blocks`のインデックス。選択なしはnull。 */
  selected: number | null;
  /** ガイドで注目させるpalette state集合。指定中はそれ以外のブロックが沈む。 */
  highlight?: ReadonlySet<number> | null;
  /** かんせい画面用のゆっくり自動回転。 */
  autoRotate?: boolean;
  onError: (message: string | null) => void;
  onSelect: (index: number | null) => void;
  onStats?: (stats: ViewerStats) => void;
}

interface Engine {
  renderer: THREE.WebGLRenderer;
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
  controls: OrbitControls;
  content: THREE.Group;
  fog: THREE.Fog;
}

/** CSS側の空グラデーションの地平線に合わせたフォグ色。 */
const HORIZON_COLOR = 0xb9d3f6;

function toBufferGeometry(data: GeometryData): THREE.BufferGeometry {
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(data.positions, 3));
  geometry.setAttribute('uv', new THREE.BufferAttribute(data.uvs, 2));
  geometry.setAttribute('color', new THREE.BufferAttribute(data.colors, 3));
  geometry.setIndex(new THREE.BufferAttribute(data.indices, 1));
  geometry.computeBoundingSphere();
  return geometry;
}

export function StructureViewer({ model, mode, pack, selected, highlight, autoRotate, onError, onSelect, onStats }: StructureViewerProps) {
  const hostRef = useRef<HTMLDivElement>(null);
  const engineRef = useRef<Engine | null>(null);
  const glowMaterialRef = useRef<THREE.MeshBasicMaterial | null>(null);
  const onErrorRef = useRef(onError);
  const onSelectRef = useRef(onSelect);
  const onStatsRef = useRef(onStats);
  onErrorRef.current = onError;
  onSelectRef.current = onSelect;
  onStatsRef.current = onStats;

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    let renderer: THREE.WebGLRenderer;
    try {
      renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    } catch {
      onErrorRef.current('この端末ではWebGLを開始できませんでした。ブラウザのハードウェアアクセラレーションを確認してください。');
      return;
    }
    onErrorRef.current(null);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.domElement.setAttribute('role', 'img');
    renderer.domElement.className = 'viewer-canvas';
    host.append(renderer.domElement);

    const scene = new THREE.Scene();
    const fog = new THREE.Fog(HORIZON_COLOR, 60, 220);
    scene.fog = fog;

    const camera = new THREE.PerspectiveCamera(55, 1, 0.1, 2000);
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.07;
    controls.screenSpacePanning = true;
    controls.minDistance = 1.5;
    controls.maxDistance = 600;

    const content = new THREE.Group();
    scene.add(content);
    engineRef.current = { renderer, scene, camera, controls, content, fog };

    const resize = () => {
      const width = Math.max(1, host.clientWidth);
      const height = Math.max(1, host.clientHeight);
      renderer.setSize(width, height, false);
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
    };
    const resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(host);
    resize();

    const raycaster = new THREE.Raycaster();
    const pointer = new THREE.Vector2();
    let pressedAt: { x: number; y: number } | null = null;

    const handlePointerDown = (event: PointerEvent) => {
      pressedAt = { x: event.clientX, y: event.clientY };
    };
    const handlePointerUp = (event: PointerEvent) => {
      const pressed = pressedAt;
      pressedAt = null;
      // 回転やパンのドラッグを選択と取り違えない。
      if (!pressed || Math.hypot(event.clientX - pressed.x, event.clientY - pressed.y) > 6) return;

      const rect = renderer.domElement.getBoundingClientRect();
      pointer.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      pointer.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera(pointer, camera);

      const hit = raycaster
        .intersectObject(content, true)
        .find((item) => item.faceIndex !== undefined && item.object.userData.blockIndices);
      if (!hit) {
        onSelectRef.current(null);
        return;
      }
      const blockIndices = hit.object.userData.blockIndices as Uint32Array;
      const quad = Math.floor((hit.faceIndex ?? 0) / 2);
      onSelectRef.current(quad < blockIndices.length ? blockIndices[quad] : null);
    };

    renderer.domElement.addEventListener('pointerdown', handlePointerDown);
    renderer.domElement.addEventListener('pointerup', handlePointerUp);

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    let frame = 0;
    const render = (time: number) => {
      controls.update();
      // ガイドのハイライトは、動きを減らす設定でなければゆっくり明滅させて目を引く。
      const glow = glowMaterialRef.current;
      if (glow) glow.opacity = reducedMotion.matches ? 0.24 : 0.16 + 0.12 * (Math.sin(time / 300) + 1) / 2;
      renderer.render(scene, camera);
      frame = requestAnimationFrame(render);
    };
    render(performance.now());

    return () => {
      cancelAnimationFrame(frame);
      resizeObserver.disconnect();
      renderer.domElement.removeEventListener('pointerdown', handlePointerDown);
      renderer.domElement.removeEventListener('pointerup', handlePointerUp);
      controls.dispose();
      renderer.dispose();
      renderer.domElement.remove();
      engineRef.current = null;
    };
  }, []);

  useEffect(() => {
    const engine = engineRef.current;
    if (!engine) return;

    engine.renderer.domElement.setAttribute('aria-label', `${model.name}の3Dプレビュー`);

    const assets = buildStructureAssets(model, pack);
    const geometry = buildStructureGeometry(model, assets.appearances, assets.atlas, { mode, focus: highlight });

    const texture = new THREE.DataTexture(
      new Uint8Array(assets.atlas.data.buffer, assets.atlas.data.byteOffset, assets.atlas.data.length),
      assets.atlas.width,
      assets.atlas.height,
      THREE.RGBAFormat,
    );
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.magFilter = THREE.NearestFilter;
    texture.minFilter = THREE.NearestFilter;
    texture.generateMipmaps = false;
    texture.needsUpdate = true;

    const textured = mode === 'texture';
    const materials: THREE.Material[] = [];
    const geometries: THREE.BufferGeometry[] = [];
    const meshes: THREE.Mesh[] = [];

    if (geometry.solid) {
      const material = new THREE.MeshBasicMaterial({
        map: textured ? texture : null,
        vertexColors: true,
        alphaTest: textured ? 0.5 : 0,
      });
      const bufferGeometry = toBufferGeometry(geometry.solid);
      const mesh = new THREE.Mesh(bufferGeometry, material);
      mesh.userData.blockIndices = geometry.solid.blockIndices;
      meshes.push(mesh);
      materials.push(material);
      geometries.push(bufferGeometry);
    }
    if (geometry.translucent) {
      const material = new THREE.MeshBasicMaterial({
        map: textured ? texture : null,
        vertexColors: true,
        transparent: true,
        opacity: textured ? 1 : 0.62,
        depthWrite: true,
      });
      const bufferGeometry = toBufferGeometry(geometry.translucent);
      const mesh = new THREE.Mesh(bufferGeometry, material);
      mesh.userData.blockIndices = geometry.translucent.blockIndices;
      mesh.renderOrder = 1;
      meshes.push(mesh);
      materials.push(material);
      geometries.push(bufferGeometry);
    }

    meshes.forEach((mesh) => engine.content.add(mesh));
    onStatsRef.current?.({
      faces: geometry.faceCount,
      textures: assets.textureCount,
      sourced: assets.atlas.sourced,
    });

    return () => {
      meshes.forEach((mesh) => mesh.removeFromParent());
      geometries.forEach((item) => item.dispose());
      materials.forEach((item) => item.dispose());
      texture.dispose();
    };
  }, [model, mode, pack, highlight]);

  // ガイドで注目させるブロック全部に、薄い光の箱をまとめて1ドローコールで被せる。
  useEffect(() => {
    const engine = engineRef.current;
    if (!engine || !highlight || highlight.size === 0) return;

    const targets = model.blocks.filter((block) => highlight.has(block.state));
    if (targets.length === 0) return;

    const box = new THREE.BoxGeometry(1.06, 1.06, 1.06);
    const material = new THREE.MeshBasicMaterial({
      color: 0xffe066,
      transparent: true,
      opacity: 0.24,
      depthWrite: false,
    });
    const mesh = new THREE.InstancedMesh(box, material, targets.length);
    mesh.renderOrder = 3;
    // タップでブロックを調べる既存のRaycasterに拾われないようにする。
    mesh.raycast = () => {};
    const matrix = new THREE.Matrix4();
    targets.forEach((block, index) => {
      matrix.setPosition(
        block.position[0] - model.size[0] / 2 + 0.5,
        block.position[1] - model.size[1] / 2 + 0.5,
        block.position[2] - model.size[2] / 2 + 0.5,
      );
      mesh.setMatrixAt(index, matrix);
    });
    mesh.instanceMatrix.needsUpdate = true;
    engine.scene.add(mesh);
    glowMaterialRef.current = material;

    return () => {
      glowMaterialRef.current = null;
      mesh.removeFromParent();
      mesh.dispose();
      box.dispose();
      material.dispose();
    };
  }, [model, highlight]);

  useEffect(() => {
    const engine = engineRef.current;
    if (!engine) return;
    // 動きを減らす設定の端末では、かんせい画面でも勝手に回さない。
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const apply = () => {
      engine.controls.autoRotate = (autoRotate ?? false) && !reducedMotion.matches;
    };
    engine.controls.autoRotateSpeed = 1.6;
    apply();
    reducedMotion.addEventListener('change', apply);
    return () => {
      reducedMotion.removeEventListener('change', apply);
      engine.controls.autoRotate = false;
    };
  }, [autoRotate]);

  useEffect(() => {
    const engine = engineRef.current;
    if (!engine) return;

    const span = Math.max(...model.size);
    const gridSize = Math.max(12, Math.ceil((span * 1.35) / 2) * 2);
    const groundY = -model.size[1] / 2;

    const groundGeometry = new THREE.PlaneGeometry(gridSize, gridSize);
    const groundMaterial = new THREE.MeshBasicMaterial({
      color: 0x86b055,
      transparent: true,
      opacity: 0.55,
      depthWrite: false,
      side: THREE.DoubleSide,
    });
    const ground = new THREE.Mesh(groundGeometry, groundMaterial);
    ground.rotation.x = -Math.PI / 2;
    ground.position.y = groundY - 0.01;
    ground.renderOrder = -1;
    engine.scene.add(ground);

    const grid = new THREE.GridHelper(gridSize, gridSize, 0x4f6b3f, 0x5f7d4c);
    grid.position.y = groundY;
    (grid.material as THREE.Material).transparent = true;
    (grid.material as THREE.Material).opacity = 0.4;
    engine.scene.add(grid);

    engine.fog.near = span * 2.2;
    engine.fog.far = span * 8 + 120;
    engine.controls.maxDistance = Math.max(120, span * 8);

    const distance = Math.max(7, span * 1.15);
    engine.camera.position.set(distance, distance * 0.62, distance);
    engine.controls.target.set(0, 0, 0);
    engine.controls.update();

    return () => {
      grid.removeFromParent();
      grid.dispose();
      ground.removeFromParent();
      groundGeometry.dispose();
      groundMaterial.dispose();
    };
  }, [model]);

  useEffect(() => {
    const engine = engineRef.current;
    const block = selected === null ? undefined : model.blocks[selected];
    if (!engine || !block) return;

    const box = new THREE.BoxGeometry(1, 1, 1);
    const edges = new THREE.EdgesGeometry(box);
    const inner = new THREE.LineSegments(edges, new THREE.LineBasicMaterial({ color: 0x14180f }));
    const outer = new THREE.LineSegments(edges, new THREE.LineBasicMaterial({ color: 0xf4ffe0 }));
    inner.scale.setScalar(1.03);
    outer.scale.setScalar(1.06);

    const glowMaterial = new THREE.MeshBasicMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 0.25,
      depthWrite: false,
    });
    const glow = new THREE.Mesh(box, glowMaterial);
    glow.scale.setScalar(1.045);
    glow.renderOrder = 2;

    const outline = new THREE.Group();
    outline.add(glow, inner, outer);
    outline.position.set(
      block.position[0] - model.size[0] / 2 + 0.5,
      block.position[1] - model.size[1] / 2 + 0.5,
      block.position[2] - model.size[2] / 2 + 0.5,
    );
    engine.scene.add(outline);

    return () => {
      outline.removeFromParent();
      box.dispose();
      edges.dispose();
      glowMaterial.dispose();
      (inner.material as THREE.Material).dispose();
      (outer.material as THREE.Material).dispose();
    };
  }, [model, selected]);

  return <div className="viewer-host" ref={hostRef} data-testid="viewer-host" />;
}
